import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ConsultasEletivas, VinculoUsuarioOperadora } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString, pseudonimizar } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, TipoDominio, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { GuiaMonitorActionCellRenderer } from '@gestor-web/app/pages/private/criticas/criticas-geradas/guia-monitor-action-cell.component';
import { AgFilterUtils, LongColumnHintComponent, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { SelectItem } from 'primeng/api';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { first, map, mergeMap, takeUntil } from 'rxjs/operators';
import { ConsultasMedicasEletivasService } from './consultas-medicas-eletivas.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const GRID_KEY = 'ANALISES.CONSULTAS_MEDICAS_ELETIVAS';

const COLUMNS: ColDef[] = [
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
    cellRenderer: GuiaMonitorActionCellRenderer,
    width: 150,
    sort: 'asc',
  },
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 100,
  },
  {
    headerName: '024-Nr. guia operadora',
    field: 'nr_guia_operadora',
    type: 'str',
    width: 100,
  },
  {
    headerName: 'Nr. guia prestador',
    field: 'nr_guia_prestador',
    type: 'str',
    width: 100,
  },
  {
    headerName: '025-ID reembolso ou pagto eventual',
    field: 'nr_reembolso',
    type: 'str',
    width: 150,
  },
  {
    headerName: '013-Tipo de identificação - CPF/CNPJ',
    field: 'tp_cnpj_cpf_exec',
    width: 100,
  },
  {
    headerName: '014-CNPJ/CPF Executante',
    field: 'nr_cnpj_cpf_exec',
    width: 100,
    type: 'cpfCnpj',
  },
  {
    headerName: '012-Cód. CNES executante',
    field: 'nr_cnes_exec',
    width: 100,
  },
  {
    headerName: '080-Data processamento',
    field: 'dt_processamento',
    type: 'date',
  },
  {
    headerName: '029-Data realização ou inicio atendimento',
    field: 'dt_realizacao',
    type: 'date',
  },
  {
    headerName: '022-Origem da guia',
    field: 'origem_guia',
    width: 200,
  },
  {
    headerName: '021-Tipo de guia',
    field: 'tipo_guia',
    width: 100,
  },
  {
    headerName: '034-Tipo de consulta',
    field: 'tipo_consulta',
    width: 100,
  },
  {
    headerName: 'Status da Guia na ANS',
    field: 'status_guia_ans',
  },
  {
    headerName: 'Nome do estabelecimento no CNES',
    field: 'nm_estabelecimento_cnes',
    width: 200,
  },
  {
    headerName: 'CPF do médico',
    field: 'nr_cpf_profissional',
    type: 'cpf',
  },
  {
    headerName: 'Nome do médico',
    field: 'nm_medico',
  },
  {
    headerName: '035-Cód. CBO executante',
    field: 'nr_cbo_exec',
  },
  {
    headerName: 'CBO do Executante na Guia',
    field: 'ds_titulo_cbo_exec',
  },
  {
    headerName: 'CBO / Especialidade do Médico no CFM',
    field: 'lt_cbo_especialidade_medico_conselho',
    width: 200,
  },
  {
    headerName: 'CRM/UF do médico',
    field: 'lt_crm_uf_medico',
  },
  {
    headerName: 'Cód. beneficiário no SIB',
    field: 'nr_cartao_beneficiario',
  },
  {
    headerName: '096-CNS do beneficiário',
    field: 'nr_cns',
    hide: true,
  },
  {
    headerName: '126-Nr. CPF do beneficiário',
    field: 'nr_cpf_beneficiario',
    type: 'cpf',
    hide: true,
  },
  {
    headerName: 'Idade em anos',
    field: 'qt_ano_idade',
    type: 'int',
    width: 100,
  },
  {
    headerName: 'ID faixa etaria',
    field: 'id_faixa_etaria',
    hide: true,
  },
  {
    headerName: 'Qtde. críticas pendentes',
    field: 'qt_critica_pendente',
    type: 'int',
    width: 120,
  },
  {
    headerName: 'Campos com críticas',
    field: 'lt_campo_critica',
    width: 200,
    cellRenderer: LongColumnHintComponent,
  },
  {
    headerName: 'Críticas nas guias (capa)',
    field: 'lt_critica_guia',
    width: 200,
    cellRenderer: LongColumnHintComponent,
  },
  {
    headerName: 'Críticas nos itens das guias (procedimentos/itens assistenciais)',
    field: 'lt_critica_itens',
    width: 300,
    cellRenderer: LongColumnHintComponent,
  },
  {
    headerName: 'Qtde. guias',
    field: 'count',
    aggFunc: 'count',
    hide: true,
  },
];

@Component({
    templateUrl: './consultas-medicas-eletivas.component.html',
    styleUrls: ['./consultas-medicas-eletivas.component.scss'],
    providers: [ConsultasMedicasEletivasService, DecimalPipe],
    standalone: false
})
export class ConsultasMedicasEletivasComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<RowData>;

  gridOptions: GridOptions<RowData>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  anosBasesOptions: number[] = [];
  anoBase: number = null;

  faixasEtariasOptions: SelectItem<number>[] = [];
  faixaEtaria: number = null;

  criticaPendente: boolean = true;

  rowData: RowData[];
  rowDataOriginal: RowData[];

  pseudonimizarAction: boolean = true;
  pseudonimizarActionVisible: boolean = true;

  detalheGuiaDisplay = false;
  detalheGuiaCodigo: string;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: ConsultasMedicasEletivasService,
    private geralService: GeralService,
    private gridService: GridService,
    private decimalPipe: DecimalPipe,
    private competenciaPipe: CompetenciaPipe,
    private datePipe: DatePipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private notificationService: NotificationService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    combineLatest([
      this.permissaoService.checkAcessoFeature(FUNCIONALIDADE.DESLIGAR_PSEUDONIMIZACAO),
      this.store.select(OperadorasSelectors.selectWorkspaceOperadora),
    ])
      .pipe(
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        mergeMap(([[podeDesligarPseudonimizacao, operadora], params]) =>
          this.geralService
            .getAnosBases(operadora.cd_registro_ans)
            .pipe(map(anosBases => ({ podeDesligarPseudonimizacao, operadora, params, anosBases }))),
        ),
        mergeMap(({ podeDesligarPseudonimizacao, operadora, params, anosBases }) =>
          this.geralService
            .getDominioPorTipo(TipoDominio.IND_FAIXA_ETARIA_INDICADOR_CONSULTA)
            .pipe(map(faixasEtarias => ({ podeDesligarPseudonimizacao, operadora, params, anosBases, faixasEtarias }))),
        ),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(({ podeDesligarPseudonimizacao, operadora, params, anosBases, faixasEtarias }) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];
        this.anosBasesOptions = anosBases;
        if (anosBases.length) {
          let lastAno = anosBases[0];
          if (lastAno > ano && anosBases.find(a => a == ano)) {
            lastAno = ano;
          }
          setTimeout(() => {
            this.anoBase = lastAno;
          });
        }

        this.faixasEtariasOptions = faixasEtarias.map(item => ({
          value: +item.cd_valor_valido,
          label: item.ds_exibicao,
        }));
        this.faixaEtaria = this.faixasEtariasOptions.length ? this.faixasEtariasOptions[0].value : null;

        if (operadora.ib_dado_sensivel_pseudonimizado) {
          this.pseudonimizarActionVisible = false;
        } else {
          const vinculo = operadora as VinculoUsuarioOperadora;
          this.pseudonimizarActionVisible = podeDesligarPseudonimizacao || !vinculo.ib_dado_sensivel_pseudonimizado_usuario;
        }

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<RowData>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          date: this.datePipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        exportExcelFn: () => this.exportExcel(),
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, anoBase, faixaEtaria, criticaPendente, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (anoBase) {
      hasFilter = true;
      this.anoBase = +anoBase;
    }

    if (faixaEtaria) {
      hasFilter = true;
      this.faixaEtaria = +faixaEtaria;
    }

    if (criticaPendente != null) {
      hasFilter = true;
      this.criticaPendente = !!criticaPendente;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    // Trata o comportamento do componente visual do carregamento
    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1] || !this.anoBase) {
      this.rowData = [];
      this.rowDataOriginal = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    this.service
      .getData(
        this.operadora,
        this.competenciaRange[0],
        this.competenciaRange[1],
        this.anoBase,
        this.faixaEtaria,
        this.criticaPendente,
      )
      .subscribe({
        next: items => {
          if (!items?.length) {
            this.rowData = [];
            this.rowDataOriginal = [];
            this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
            return;
          }

          this.rowDataOriginal = items.map(item => {
            if (!item.tp_cnpj_cpf_exec) {
              item.tp_cnpj_cpf_exec = this.geralService.buildNomeDeDominio(item.id_cnpj_cpf_exec, 'MON_ID_CNPJ_CPF');
            }
            if (!item.origem_guia) {
              item.origem_guia = this.geralService.buildNomeDeDominio(
                item.id_origem_guia,
                'dm_origemEventoAtencaoSaude',
              );
            }
            if (!item.tipo_guia) {
              item.tipo_guia = this.geralService.buildNomeDeDominio(item.tp_guia, 'dm_tipoEventoMonitoramento');
            }
            if (!item.tipo_consulta) {
              item.tipo_consulta = this.geralService.buildNomeDeDominio(item.tp_consulta, 'dm_tipoConsulta');
            }
            if (!item.status_guia_ans) {
              item.status_guia_ans = this.geralService.buildNomeDeDominio(item.st_guia_ans, 'MON_GUIA_SITUACAO_ANS');
            }

            this.geralService.buildNomeDeDominio;
            return {
              ...item,
              count: 1,
            };
          });

          this.pseudonimizarData();

          const totalData = items.reduce(
            (acc, item) => ({
              qt_critica_pendente: acc.qt_critica_pendente + item.qt_critica_pendente,
            }),
            { qt_critica_pendente: 0 },
          );
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', [totalData]);
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGrid?.api?.setGridOption('loading', false),
      });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );

    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  togglePseudonimizar() {
    this.pseudonimizarAction = !this.pseudonimizarAction;
    this.pseudonimizarData();
  }

  private pseudonimizarData() {
    if (!this.pseudonimizarActionVisible || !this.pseudonimizarAction) {
      this.rowData = this.rowDataOriginal;
      return;
    }

    this.rowData = this.rowDataOriginal?.map(row => {
      return {
        ...row,
        nr_cpf_beneficiario: pseudonimizar(row.nr_cpf_beneficiario),
        nr_cns: pseudonimizar(row.nr_cns),
        nr_cpf_profissional: pseudonimizar(row.nr_cpf_profissional),
        nr_cnpj_cpf_exec: pseudonimizar(row.nr_cnpj_cpf_exec),
      };
    });
  }

  exportExcel(): void {
    const fileNamePrefix = `Consultas-Medicas-Eletivas-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const headers = [
      `Consultas médicas eletivas - Visão ${visao?.nm_visao}`,
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
      'Ano de realização: ' + this.anoBase,
      'Faixa etária: ' + this.faixasEtariasOptions.find(faixa => faixa.value == this.faixaEtaria).label,
      'Crítica pendente: ' + (this.criticaPendente ? 'Sim' : 'Não'),
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  showDetalheGuiaFor(cd_guia_monitor: string) {
    // console.log('showDetalheGuiaFor', cd_guia_monitor);
    this.detalheGuiaDisplay = true;
    this.detalheGuiaCodigo = cd_guia_monitor;
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      anoBase: this.anoBase,
      faixaEtaria: this.faixaEtaria,
      criticaPendente: this.criticaPendente ? 1 : 0,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

export interface RowData extends ConsultasEletivas {
  count: number;
}
